import React from 'react';
import { Link } from 'react-router-dom';

interface Product {
  id: string;
  name: string;
  tagline: string;
  description: string;
  icon: React.ReactNode;
  features: string[];
  color: 'primary' | 'accent';
  price: string;
}

const products: Product[] = [
  {
    id: 'cimmpleflow',
    name: 'CimmpleFlow',
    tagline: 'Manufacturing & Logistics Made Simple',
    description: 'Complete Information on Manufacturing Parts, Planning and Logistics Evaluation. A cloud-based ERP solution that gives you full visibility and control over your business operations - anytime, anywhere.',
    icon: (
      <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
      </svg>
    ),
    features: [
      'Quotations (VendorConnect™)',
      'Order Management',
      'Purchasing',
      'AI-powered invoice extraction (PDF & email)',
      'Job Management (with live tracking)',
      'Quality Control (live reporting)',
      'Reporting (with scheduling)',
      'Accounting (advanced)',
      'Timesheets (3rd party integration)',
      'Document Storage (version control)',
    ],
    color: 'primary',
    price: 'From $49/user/month',
  },
  {
    id: 'cimmlepay',
    name: 'CimmplePay',
    tagline: 'Payroll Processing, Simplified',
    description: 'An intelligent payroll management solution that automates salary processing, tax calculations, and compliance reporting, so you can focus on what matters most - your people.',
    icon: (
      <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    features: [
      'Automated salary calculations',
      'Tax compliance & filing',
      'Employee self-service portal',
      'Biometric face-recognition attendance',
      'Leave & attendance tracking',
      'Comprehensive reporting',
    ],
    color: 'accent',
    price: '$19/month',
  },
];

const Products: React.FC = () => {
  return (
    <div className="pt-24">
      {/* Hero Section */}
      <section className="pt-8 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-block px-4 py-1 rounded-full bg-primary-100 dark:bg-primary-500/10 text-primary-700 dark:text-primary-400 text-sm font-medium mb-3">
            Our Products
          </span>
          <h1 className="text-3xl md:text-4xl font-display font-bold text-slate-900 dark:text-white mb-3">
            Software solutions that <span className="gradient-text">work for you</span>
          </h1>
          <p className="text-slate-700 dark:text-dark-300 max-w-2xl mx-auto">
            Powerful, cloud-based products designed to simplify your business operations.
          </p>
        </div>
      </section>

      {/* Products Cards */}
      <section className="pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            {products.map((product) => (
              <Link
                key={product.id}
                to={`/products/${product.id}`}
                className={`group card p-8 hover:scale-[1.02] transition-all duration-300 ${
                  product.color === 'primary' 
                    ? 'hover:border-primary-500/50 hover:shadow-xl hover:shadow-primary-500/10' 
                    : 'hover:border-accent-500/50 hover:shadow-xl hover:shadow-accent-500/10'
                }`}
              >
                {/* Header */}
                <div className="flex items-start gap-4 mb-6">
                  <div className={`w-16 h-16 rounded-2xl flex-shrink-0 ${
                    product.color === 'primary' 
                      ? 'bg-primary-100 dark:bg-primary-500/10 text-primary-600 dark:text-primary-400' 
                      : 'bg-accent-100 dark:bg-accent-500/10 text-accent-600 dark:text-accent-400'
                  } flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}>
                    {product.icon}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h2 className="text-2xl font-display font-bold text-slate-900 dark:text-white">
                        {product.name}
                      </h2>
                    </div>
                    <p className={`text-sm font-medium ${
                      product.color === 'primary' ? 'text-primary-600 dark:text-primary-400' : 'text-accent-600 dark:text-accent-400'
                    }`}>
                      {product.tagline}
                    </p>
                  </div>
                </div>

                {/* Description */}
                <p className="text-slate-700 dark:text-dark-300 mb-6 line-clamp-2">
                  {product.description}
                </p>

                {/* Features Preview */}
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-slate-500 dark:text-dark-400 uppercase tracking-wider mb-3">
                    Key Features
                  </h3>
                  <div className="grid grid-cols-2 gap-2">
                    {product.features.slice(0, 4).map((feature, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm text-slate-700 dark:text-dark-300">
                        <svg className={`w-4 h-4 flex-shrink-0 ${
                          product.color === 'primary' ? 'text-primary-600 dark:text-primary-500' : 'text-accent-600 dark:text-accent-500'
                        }`} fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        <span className="truncate">{feature.split('(')[0].trim()}</span>
                      </div>
                    ))}
                  </div>
                  {product.features.length > 4 && (
                    <p className={`text-sm mt-2 font-medium ${
                      product.color === 'primary' ? 'text-primary-600 dark:text-primary-400' : 'text-accent-600 dark:text-accent-400'
                    }`}>
                      +{product.features.length - 4} more features
                    </p>
                  )}
                </div>

                {/* Price & CTA */}
                <div className="flex items-center justify-between pt-4 border-t border-slate-200 dark:border-dark-800">
                  <span className="text-slate-900 dark:text-white font-semibold">{product.price}</span>
                  <div className={`flex items-center gap-2 font-semibold ${
                    product.color === 'primary' ? 'text-primary-600 dark:text-primary-400' : 'text-accent-600 dark:text-accent-400'
                  } group-hover:gap-4 transition-all duration-300`}>
                    Learn more
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="card p-12 text-center relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-primary-500/10 to-accent-500/10" />
            <div className="relative">
              <h2 className="text-3xl md:text-4xl font-display font-bold text-slate-900 dark:text-white mb-4">
                Ready to get started?
              </h2>
              <p className="text-slate-700 dark:text-dark-300 text-lg mb-8 max-w-xl mx-auto">
                Try our products free or contact us to learn more about how they can transform your business.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link to="/pricing" className="btn-primary">
                  View Pricing
                </Link>
                <Link to="/contact" className="btn-secondary">
                  Contact Sales
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Products;
